// src/pages/PlaybookPage.jsx
// Legacy playbook page - now uses shared DataProductPlaybookLayout
// ============================================================
// CLIENT ID: AuthContext session.client_id is the PRIMARY source
// REMOVED: useSearchParams for client_id - AuthContext is source of truth
// URL params kept only for 'name' (display name for sharing)
// ============================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DataProductPlaybookLayout } from '../components/playbook/DataProductPlaybookLayout';
import { RefreshCw, AlertCircle } from 'lucide-react';

// Export Button Component
const ExportButton = ({ clientName, clientId }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Simulate PDF export - in real implementation, this would:
      // 1. Generate PDF using react-to-pdf or similar
      // 2. Apply print stylesheets
      // 3. Download the file
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('PDF export completed for client:', clientId);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      style={{
        backgroundColor: '#FF6E4C',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: isExporting ? 'not-allowed' : 'pointer',
        opacity: isExporting ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease'
      }}
    >
      {isExporting ? (
        <>
          <span>⏳</span>
          Exporting...
        </>
      ) : (
        <>
          <span>📄</span>
          Export to PDF
        </>
      )}
    </button>
  );
};

// Share Button Component
const ShareButton = ({ clientName, clientId }) => {
  const [isSharing, setIsSharing] = useState(false);

  const handleShare = async () => {
    setIsSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${clientName} - Data Product Playbook`,
          text: 'Strategic data monetization plan',
          url: window.location.href
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <button
      onClick={handleShare}
      disabled={isSharing}
      style={{
        backgroundColor: '#18365E',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: isSharing ? 'not-allowed' : 'pointer',
        opacity: isSharing ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease'
      }}
    >
      {isSharing ? (
        <>
          <span>⏳</span>
          Sharing...
        </>
      ) : (
        <>
          <span>🔗</span>
          Share Link
        </>
      )}
    </button>
  );
};

// Main PlaybookPage Component
const PlaybookPage = () => {
  const navigate = useNavigate();
  
  // ============================================================
  // CLIENT ID: AuthContext is the ONLY source for client_id
  // REMOVED: useSearchParams for client_id
  // ============================================================
  const { session, sessionLoading } = useAuth();
  const clientId = session?.client_id || null;
  
  // Get client name from session/profile
  const clientName = session?.client_name || 'Your Organization';

  // Loading state - wait for session
  if (sessionLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <RefreshCw className="w-8 h-8 animate-spin mb-4" style={{ color: '#18365E' }} />
        <p style={{ color: '#64748b', fontSize: 14 }}>Loading your session...</p>
      </div>
    );
  }

  // No client ID available
  if (!clientId) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24
      }}>
        <AlertCircle className="w-12 h-12 mb-4" style={{ color: '#f59e0b' }} />
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#18365E', marginBottom: 8 }}>
          No Organization Selected
        </h2>
        <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', maxWidth: 400, marginBottom: 16 }}>
          Your account is not currently associated with an organization. 
          Please contact your administrator or complete onboarding to view the playbook.
        </p>
        <button
          onClick={() => navigate('/home')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#18365E',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '24px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Client Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#18365E',
                margin: 0,
                fontFamily: 'Montserrat, sans-serif'
              }}>
                {clientName} – Data Product Playbook
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: '4px 0 0 0'
              }}>
                Strategic data monetization plan
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <ShareButton clientName={clientName} clientId={clientId} />
            <ExportButton clientName={clientName} clientId={clientId} />
          </div>
        </div>
      </div>

      {/* Main Content - Embedded Playbook Layout */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <DataProductPlaybookLayout 
          clientId={clientId}
          showTitle={false}
        />
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          /* Hide header and buttons for print */
          header, .action-buttons {
            display: none !important;
          }
          
          /* Optimize typography for print */
          body {
            font-size: 12pt;
            line-height: 1.4;
          }
          
          h1, h2, h3 {
            page-break-after: avoid;
          }
          
          section {
            page-break-inside: avoid;
          }
        }
        
        @media (max-width: 768px) {
          .main-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PlaybookPage;